/** EMP-WS-UI-001 — right rail: Quick Actions (existing workflows only) + Employee Status (non-sensitive). */
import React from 'react';
import { Zap, IdCard, Mail, Link2, Scale, History, ChevronRight, Sparkles, CalendarDays, MapPin, Globe2, Briefcase, Users, UserCircle2, ShieldCheck } from 'lucide-react';
import { StatusPill } from './EmployeeHero';
import { formatCalendarDate } from '../../../../utils/formatters';

const Row = ({ icon: Icon, label, onClick, testid, disabled, hint }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={hint} className="pf-qa w-full flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-left hover:border-violet-200 hover:bg-violet-50/40 disabled:opacity-50 disabled:cursor-not-allowed" data-testid={testid}>
    <span className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
    <span className="flex-1 text-[13px] font-medium text-slate-800">{label}</span>
    <ChevronRight className="pf-qa-arrow h-4 w-4 text-slate-400" />
  </button>
);

export const QuickActionsCard = ({ actions, delay = 200 }) => (
  <section className="lv-card an-rise p-4" style={{ animationDelay: `${delay}ms` }} data-testid="ws-quick-actions">
    <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Zap className="h-4 w-4 text-violet-500" /> Quick Actions</h2>
    <div className="mt-3 space-y-2">{actions.map(a => <Row key={a.testid} {...a} />)}</div>
    <div className="mt-4 rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50 to-blue-50/60 px-3.5 py-3 flex items-start gap-3">
      <Sparkles className="h-5 w-5 text-violet-500 shrink-0 mt-0.5" />
      <div><p className="text-[12.5px] font-semibold text-violet-900">Keep Employee Data Up to Date</p><p className="text-[12px] leading-snug text-violet-700/90">Accurate information helps with payroll, attendance, leave management and reporting.</p></div>
    </div>
  </section>
);

export const QA_ICONS = { id: IdCard, email: Mail, link: Link2, adjustments: Scale, audit: History };

const Line = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-t border-slate-100 first:border-t-0" data-testid={testid}>
    <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-500"><Icon className="h-3.5 w-3.5 text-slate-400" />{label}</span>
    <span className="text-[13px] font-semibold text-slate-800 text-right truncate capitalize">{value || '—'}</span>
  </div>
);

export const EmployeeStatusCard = ({ employee, delay = 260 }) => (
  <section className="lv-card an-rise p-4" style={{ animationDelay: `${delay}ms` }} data-testid="ws-status-card">
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><ShieldCheck className="h-4 w-4 text-violet-500" /> Employee Status</h2>
      <StatusPill status={employee.status} testid="ws-status-pill" />
    </div>
    <div className="mt-2">
      <Line icon={CalendarDays} label="Last Working Day" value={employee.last_working_day ? formatCalendarDate(employee.last_working_day) : '—'} testid="status-last-working-day" />
      <Line icon={MapPin} label="Location" value={employee.location || employee.work_location} testid="status-location" />
      <Line icon={Globe2} label="Time Zone" testid="status-timezone"
        value={employee.resolved_attendance_timezone
          ? `${employee.resolved_attendance_timezone} (${{ employee: 'Employee', team: 'Team', company: 'Company Default', fallback: 'Default' }[employee.attendance_timezone_source] || 'Company Default'})`
          : employee.employee_timezone} />
      <Line icon={Briefcase} label="Employment Type" value={employee.employment_type} testid="status-employment-type" />
      <Line icon={Users} label="Team" value={employee.team} testid="status-team" />
      <Line icon={UserCircle2} label="Manager" value={employee.manager_name || employee.manager_employee_id} testid="status-manager" />
    </div>
  </section>
);
